import { useCallback, useMemo } from "react";
import Box from "@mui/material/Box";
import Grid from "components/primitives/Grid";
import IconButton from "@mui/material/IconButton";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import Collapse from "@mui/material/Collapse";
import * as Components from "../MeasurementsTable.components";
import { useProcessMeasurementTableRowData } from "./MeasurementsTableRow.hooks";
import type { Measurement } from "types";
import moment from "moment";
import MeasurementsTableRowMetrics from "./MeasurementsTableRowExpanded";
import RepetitionsTable from "components/Chart/ChartRenderer/TableView/AthleteProfileMeasurementsTableRows/RepetitionsTable";
import * as MeasurementsTableRowComponents from "./MeasurementsTableRow.components";
import Typography from "components/Typography";
import { useTranslation } from "react-i18next";
import ExerciseRecordingVideoItem from "components/ExerciseRecordingVideo/ExerciseRecordingVideoItem";
import useBoolean from "hooks/useBoolean";
import TimeHelper from "helpers/TimeHelper";
import Div from "components/primitives/Div";
import ThemeProvider2024 from "components/MuiThemeProvider/ThemeProvider2024";
import DeleteMeasurementAction from "./MeasurementsTableRowExpanded/DeleteMeasurementAction";
import EditMeasurementAction from "./MeasurementsTableRowExpanded/EditMeasurementAction";
import useRemoteConfig from "providers/RemoteConfigProvider/useRemoteConfig";
import logEvent from "utils/logEvent";

interface MeasurementsTableRowProps {
  measurement: Measurement;
}

function MeasurementsTableRow(props: MeasurementsTableRowProps) {
  const { measurement } = props;
  const HUB_ENABLE_VIDEO_PLAYBACK = useRemoteConfig("HUB_ENABLE_VIDEO_PLAYBACK");

  const { t } = useTranslation();
  const { value: open, toggle } = useBoolean();
  const rowData = useProcessMeasurementTableRowData(props.measurement);

  const repCount: number | undefined = useMemo(
    () => measurement.metrics.find((metric) => metric.field === "repCount")?.value,
    [measurement.metrics]
  );

  const timeLabel = useMemo(() => {
    const customCalendarFormat = {
      sameDay: `[${t("global.today_label")}]`,
      lastDay: `[${t("global.yesterday_label")}]`,
      lastWeek: "MMM DD",
      sameElse: "MMM DD",
    };
    return TimeHelper.formatCalendarTime(moment(measurement.completedDate), customCalendarFormat);
  }, [measurement.completedDate, t]);

  const handleOnToggleMeasurementRow = useCallback(() => {
    if (!open) {
      logEvent("WORKOUT_OVERVIEW_EXPAND_MEASUREMENT_ROW");
    }
    toggle();
  }, [open, toggle]);

  return (
    <MeasurementsTableRowComponents.TableCardPaper elevation={0}>
      <Box sx={{ p: 1.5, pb: 0 }}>
        <Grid container alignItems="center" justifyContent="space-between">
          <Components.PrimaryMetricsGrid container size="grow" alignItems="center">
            <Components.GridItem label={timeLabel} value={moment(measurement.completedDate).format("HH:mm")} />
            {rowData.defaultMetric.metadata && (
              <Components.GridItem
                label={rowData.defaultMetric.metadata.name}
                value={rowData.defaultMetric.value || "-"}
              />
            )}
            {typeof repCount !== "undefined" && (
              <Components.GridItem label={t("global.reps_label_text")} value={repCount} />
            )}
            {rowData.weightMetric.metadata && (
              <Components.GridItem label={t("global.weight_label_text")} value={rowData.weightMetric.value} />
            )}
            {measurement.variant && (
              <Components.GridItem label={t("global.variant_label_text")} value={measurement.variant[0]} />
            )}
            {HUB_ENABLE_VIDEO_PLAYBACK && measurement.video ? (
              <Components.CellGrid size="grow" minWidth="20%" maxWidth={100}>
                <ThemeProvider2024>
                  <ExerciseRecordingVideoItem measurementId={measurement.id} video={measurement.video.mux} />
                </ThemeProvider2024>
              </Components.CellGrid>
            ) : null}
            <Components.GridSpacers />
          </Components.PrimaryMetricsGrid>
          <Grid>
            <Box>
              <IconButton
                size="small"
                aria-label={t("global.expand_row_aria_label")}
                onClick={handleOnToggleMeasurementRow}
              >
                {!open ? <KeyboardArrowDownIcon color="primary" /> : <KeyboardArrowUpIcon color="primary" />}
              </IconButton>
            </Box>
          </Grid>
        </Grid>
        <Collapse in={open} unmountOnExit>
          <MeasurementsTableRowMetrics exercise={rowData.metadata} measurement={measurement} />
          {measurement.note && (
            <Box sx={{ pt: 2, pb: 1 }}>
              <Typography variant="h4" fontWeight={500}>
                {t("measurements_table_row.note_section_title")}
              </Typography>
              <Box sx={{ pb: 1 }} />
              <Typography variant="h4" title={measurement.note}>
                {props.measurement.note}
              </Typography>
            </Box>
          )}
          {Boolean(measurement.repetitions.length && rowData.metadata) && (
            <RepetitionsTable
              repetitionMetadata={rowData.metadata?.repetitions || []}
              repetitions={measurement.repetitions}
            />
          )}
          <ThemeProvider2024>
            <Div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", padding: "12px 0" }}>
              <Div sx={{ display: "flex", gap: 1 }}>
                <DeleteMeasurementAction measurementId={measurement.id} />
                <EditMeasurementAction measurement={measurement} />
              </Div>
            </Div>
          </ThemeProvider2024>
        </Collapse>
      </Box>
    </MeasurementsTableRowComponents.TableCardPaper>
  );
}

export default MeasurementsTableRow;
